/**
 * Chat Manager Page
 * Admin dashboard for managing socket server media files
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardHeader, CardBody, CardTitle,
    Row, Col, Table, Button, Badge,
    Progress, Collapse, Modal, ModalHeader, ModalBody,
    FormGroup, Label, Input, Spinner, Alert
} from 'reactstrap';
import Swal from 'sweetalert2';
import chatManagerService from 'services/chatManager.service';

function ChatManager() {
    // Server status state
    const [serverStatus, setServerStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    // Media stats state
    const [mediaStats, setMediaStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    // Settings state
    const [settings, setSettings] = useState({ autoDelete: { enabled: false, days: 30 } });
    const [savingSettings, setSavingSettings] = useState(false);

    // Expanded dates (for collapse)
    const [expandedDates, setExpandedDates] = useState({});

    // Media files by date
    const [mediaByDate, setMediaByDate] = useState({});
    const [loadingMedia, setLoadingMedia] = useState({});

    // Lightbox state
    const [lightbox, setLightbox] = useState(null);

    // Auto-delete day options
    const dayOptions = [5, 10, 15, 20, 25, 30, 90, 180];

    // Load initial data
    useEffect(() => {
        loadServerStatus();
        loadMediaStats();
        loadSettings();
    }, []);

    const loadServerStatus = async () => {
        try {
            setLoadingStatus(true);
            const data = await chatManagerService.getServerStatus();
            setServerStatus(data);
        } catch (err) {
            console.error('Load server status error:', err);
        } finally {
            setLoadingStatus(false);
        }
    };

    const loadMediaStats = async () => {
        try {
            setLoadingStats(true);
            const data = await chatManagerService.getMediaStats();
            setMediaStats(data);
        } catch (err) {
            console.error('Load media stats error:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    const loadSettings = async () => {
        try {
            const data = await chatManagerService.getSettings();
            setSettings(data);
        } catch (err) {
            console.error('Load settings error:', err);
        }
    };

    const saveSettings = async (newSettings) => {
        try {
            setSavingSettings(true);
            await chatManagerService.updateSettings(newSettings.autoDelete);
            setSettings(newSettings);
            Swal.fire({ icon: 'success', title: 'Đã lưu cài đặt!', timer: 1500, showConfirmButton: false });
        } catch (err) {
            console.error('Save settings error:', err);
            Swal.fire({ icon: 'error', title: 'Lỗi', text: err.message });
        } finally {
            setSavingSettings(false);
        }
    };

    const toggleDateExpand = async (date) => {
        const newExpanded = { ...expandedDates, [date]: !expandedDates[date] };
        setExpandedDates(newExpanded);

        // Load media for date if expanding and not loaded
        if (newExpanded[date] && !mediaByDate[date]) {
            try {
                setLoadingMedia(prev => ({ ...prev, [date]: true }));
                const data = await chatManagerService.getMediaByDate(date);
                setMediaByDate(prev => ({ ...prev, [date]: data.files }));
            } catch (err) {
                console.error('Load media by date error:', err);
            } finally {
                setLoadingMedia(prev => ({ ...prev, [date]: false }));
            }
        }
    };

    const handleDeleteByDate = async (date) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Xác nhận xóa',
            text: `Bạn có chắc muốn xóa tất cả media của ngày ${formatDate(date)}?`,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        });

        if (result.isConfirmed) {
            try {
                const response = await chatManagerService.deleteMediaByDates([date]);
                Swal.fire({ icon: 'success', title: `Đã xóa ${response.deleted} files!`, timer: 1500, showConfirmButton: false });
                loadMediaStats(); // Refresh stats
                setMediaByDate(prev => ({ ...prev, [date]: [] }));
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Lỗi', text: err.message });
            }
        }
    };

    // Format helpers
    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatUptime = (seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    };

    const getProgressColor = (percent) => {
        if (percent >= 90) return 'danger';
        if (percent >= 70) return 'warning';
        return 'success';
    };

    // Render media preview based on type
    const renderMediaItem = (file) => {
        const url = chatManagerService.getMediaUrl(file.url);

        if (file.type === 'image') {
            return (
                <div
                    className="media-thumbnail"
                    style={{
                        width: '80px',
                        height: '80px',
                        backgroundImage: `url(${url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer'
                    }}
                    onClick={() => setLightbox({ url, type: 'image' })}
                />
            );
        }

        if (file.type === 'video') {
            // Check for thumbnail
            const thumbUrl = file.filename.includes('_thumb.')
                ? url
                : chatManagerService.getMediaUrl(file.url.replace(/\.[^.]+$/, '_thumb.jpg'));
            return (
                <div
                    style={{
                        width: '80px',
                        height: '80px',
                        backgroundImage: `url(${thumbUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                    onClick={() => setLightbox({ url, type: 'video' })}
                >
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px'
                    }}>
                        ▶
                    </div>
                </div>
            );
        }

        // Other files
        return (
            <div style={{
                width: '80px',
                height: '80px',
                background: '#f0f0f0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '5px'
            }}>
                <i className={`fas ${file.type === 'pdf' ? 'fa-file-pdf' : 'fa-file'}`} style={{ fontSize: '24px', color: '#666' }} />
                <small style={{ fontSize: '10px', marginTop: '4px', textAlign: 'center', wordBreak: 'break-all' }}>
                    {file.filename.slice(-10)}
                </small>
            </div>
        );
    };

    return (
        <div className="content">
            {/* Server Status Card */}
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5">
                                <i className="nc-icon nc-globe" style={{ marginRight: '8px' }} />
                                Server Status
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            {loadingStatus ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                </div>
                            ) : serverStatus ? (
                                <Row>
                                    <Col md="6">
                                        <p><strong>IP:</strong> {serverStatus.ip}</p>
                                        <p><strong>Hostname:</strong> {serverStatus.hostname}</p>
                                        <p><strong>Uptime:</strong> {formatUptime(serverStatus.uptime)}</p>
                                        <p><strong>CPU:</strong> {serverStatus.cpu?.cores} cores ({serverStatus.cpu?.model?.slice(0, 30)}...)</p>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>RAM</span>
                                                <span>{formatSize(serverStatus.memory?.used * 1024 * 1024)} / {formatSize(serverStatus.memory?.total * 1024 * 1024)} ({serverStatus.memory?.percent}%)</span>
                                            </div>
                                            <Progress value={serverStatus.memory?.percent} color={getProgressColor(serverStatus.memory?.percent)} />
                                        </div>
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span>Disk</span>
                                                <span>{serverStatus.disk?.used} GB / {serverStatus.disk?.total} GB ({serverStatus.disk?.percent}%)</span>
                                            </div>
                                            <Progress value={serverStatus.disk?.percent} color={getProgressColor(serverStatus.disk?.percent)} />
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                <Alert color="danger">Không thể kết nối đến server</Alert>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Auto-Delete Settings */}
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5">
                                <i className="nc-icon nc-settings-gear-65" style={{ marginRight: '8px' }} />
                                Auto-Delete Settings
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Row className="align-items-center">
                                <Col md="4">
                                    <div className="d-flex align-items-center">
                                        <div
                                            onClick={() => {
                                                if (savingSettings) return;
                                                const newSettings = {
                                                    ...settings,
                                                    autoDelete: { ...settings.autoDelete, enabled: !settings.autoDelete?.enabled }
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                            style={{
                                                width: '50px',
                                                height: '26px',
                                                backgroundColor: settings.autoDelete?.enabled ? '#51cbce' : '#ccc',
                                                borderRadius: '13px',
                                                position: 'relative',
                                                cursor: savingSettings ? 'not-allowed' : 'pointer',
                                                transition: 'background-color 0.3s',
                                                flexShrink: 0
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '3px',
                                                    left: settings.autoDelete?.enabled ? '27px' : '3px',
                                                    width: '20px',
                                                    height: '20px',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '50%',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    transition: 'left 0.2s ease-in-out'
                                                }}
                                            />
                                        </div>
                                        <span style={{ marginLeft: '10px', userSelect: 'none' }}>Tự động xóa files cũ</span>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-0 d-flex align-items-center">
                                        <Label className="mb-0 mr-2">Xóa files cũ hơn:</Label>
                                        <Input
                                            type="select"
                                            value={settings.autoDelete?.days || 30}
                                            onChange={(e) => {
                                                const newSettings = {
                                                    ...settings,
                                                    autoDelete: { ...settings.autoDelete, days: parseInt(e.target.value) }
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                            style={{ width: '120px' }}
                                            disabled={savingSettings || !settings.autoDelete?.enabled}
                                        >
                                            {dayOptions.map(d => (
                                                <option key={d} value={d}>{d} ngày</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="4" className="text-right">
                                    {savingSettings && <Spinner size="sm" color="primary" />}
                                    <small className="text-muted ml-2">Chạy tự động lúc 3:00 AM mỗi ngày</small>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Media Storage */}
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5">
                                <i className="nc-icon nc-album-2" style={{ marginRight: '8px' }} />
                                Media Storage
                                {mediaStats && (
                                    <Badge color="info" className="ml-2">
                                        {mediaStats.totalFiles} files • {formatSize(mediaStats.totalSize)}
                                    </Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            {loadingStats ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                </div>
                            ) : mediaStats?.byDate?.length > 0 ? (
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Ngày</th>
                                            <th>Files</th>
                                            <th>Dung lượng</th>
                                            <th style={{ width: '120px' }}>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mediaStats.byDate.map(item => (
                                            <React.Fragment key={item.date}>
                                                <tr>
                                                    <td>
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            onClick={() => toggleDateExpand(item.date)}
                                                        >
                                                            <i className={`fas fa-chevron-${expandedDates[item.date] ? 'down' : 'right'}`} />
                                                        </Button>
                                                    </td>
                                                    <td>{formatDate(item.date)}</td>
                                                    <td>{item.files}</td>
                                                    <td>{formatSize(item.size)}</td>
                                                    <td>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            outline
                                                            onClick={() => handleDeleteByDate(item.date)}
                                                        >
                                                            <i className="fas fa-trash" /> Xóa
                                                        </Button>
                                                    </td>
                                                </tr>
                                                {/* Expanded media grid */}
                                                {expandedDates[item.date] && (
                                                    <tr>
                                                        <td colSpan="5" style={{ padding: '15px', background: '#f9f9f9' }}>
                                                            {loadingMedia[item.date] ? (
                                                                <div className="text-center">
                                                                    <Spinner size="sm" color="primary" />
                                                                </div>
                                                            ) : mediaByDate[item.date]?.length > 0 ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                                    {mediaByDate[item.date].map((file, idx) => (
                                                                        <div key={idx} title={`${file.filename} (${formatSize(file.size)})`}>
                                                                            {renderMediaItem(file)}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-muted mb-0">Không có file</p>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted text-center py-4">Không có media files</p>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Lightbox Modal */}
            <Modal isOpen={!!lightbox} toggle={() => setLightbox(null)} size="lg" centered>
                <ModalHeader toggle={() => setLightbox(null)}>Preview</ModalHeader>
                <ModalBody className="text-center" style={{ background: '#000' }}>
                    {lightbox?.type === 'image' && (
                        <img
                            src={lightbox.url}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    )}
                    {lightbox?.type === 'video' && (
                        <video
                            src={lightbox.url}
                            controls
                            autoPlay
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}

export default ChatManager;
